import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Iter 92 — Route-level scroll reset.
 *
 * Mounted once inside <BrowserRouter>. On every pathname change, scrolls
 * the window (and the documentElement, for app shells that use an
 * inner-scroll container) back to the top. Fixes the issue where
 * clicking footer links from a long landing page opened the next page
 * already scrolled to the bottom.
 *
 * - Uses `behavior: "auto"` (instant) so the page never animates a
 *   visible scroll, which would be distracting on cross-page navigation.
 * - Triggers only on pathname change. The hash (`#section`) is left
 *   alone so in-page anchor links continue to work.
 * - Does not interfere with the browser's native back/forward
 *   scroll-restoration: React Router's <Link> always pushes a new
 *   history entry, and back/forward pops trigger a fresh pathname
 *   change which we honor.
 */
export default function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      // App-shell inner scroll containers (Shell.jsx uses <main> with
      // overflow-auto). Reset any element opted in via data-scroll-root.
      const el = document.querySelector("[data-scroll-root]");
      if (el) el.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch {
      // no-op — older browsers without scrollTo options fall through
      // gracefully; window.scrollTo(0,0) still applies above.
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return null;
}
